/**
 * Login with CrazyGames, the CrazyGames half of AccountProvider. Inside their frame it is the
 * only sign-in (lib/portalEmbed.ts); POST /auth/crazygames turns the player's CrazyGames token
 * into the same account session a Supabase login produces, so nothing downstream knows the
 * difference. Outside the CrazyGames frame this hook does nothing and loads nothing.
 *
 * CrazyGames' rules (docs.crazygames.com/sdk/html5-v2/user-linking):
 *  - a player logged in to CrazyGames is signed in to their linked account on arrival;
 *  - the login prompt opens only when the player asks for it, never by itself;
 *  - a CrazyGames logout reloads the page, so a stored CrazyGames session with no CrazyGames
 *    user behind it on the next load is signed out.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { exchangeCrazyGamesToken } from '../api/authApi';
import { crazyGamesUser, type CrazyGamesUser } from '../lib/crazyGamesSdk';
import { getPortal } from '../lib/portalEmbed';
import type { AccountSession } from './sessionStore';

interface CrazyGamesLoginDeps {
  sessionRef: { readonly current: AccountSession | null };
  persist: (s: AccountSession) => void;
  onNewAccount: () => void;
  onSignedOut: () => void;
}

export const CRAZYGAMES_LOGIN_UNAVAILABLE = 'Login with CrazyGames is not available right now.';

/** The SDK's error codes for a prompt the player closed or that is already open. */
const QUIET_PROMPT_ERRORS = ['userCancelled', 'showAuthPromptInProgress', 'userAlreadySignedIn'];

function promptErrorCode(error: unknown): string | null {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const { code } = error as { code: unknown };
    return typeof code === 'string' ? code : null;
  }
  return typeof error === 'string' ? error : null;
}

export function useCrazyGamesLogin({ sessionRef, persist, onNewAccount, onSignedOut }: CrazyGamesLoginDeps) {
  const enabled = getPortal() === 'crazygames';
  const [available, setAvailable] = useState(false);
  const inFlight = useRef<Promise<void> | null>(null);

  const exchange = useCallback(
    (user: CrazyGamesUser): Promise<void> => {
      if (inFlight.current) return inFlight.current;
      const attempt = (async () => {
        const module = await crazyGamesUser();
        if (!module) throw new Error(CRAZYGAMES_LOGIN_UNAVAILABLE);
        const r = await exchangeCrazyGamesToken(await module.getUserToken());
        persist({ token: r.token, accountId: r.account_id, address: r.account_id, chain: 'crazygames',
          displayName: user.username ?? null });
        if (r.is_new) onNewAccount();
      })().finally(() => {
        inFlight.current = null;
      });
      inFlight.current = attempt;
      return attempt;
    },
    [persist, onNewAccount]
  );

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      const module = await crazyGamesUser();
      if (cancelled || !module) return;
      setAvailable(true);
      module.addAuthListener((user) => {
        if (user) exchange(user).catch((error: unknown) => console.error('[crazygames] login failed', error));
      });
      const user = await module.getUser().catch(() => null);
      if (cancelled) return;
      if (user) {
        await exchange(user).catch((error: unknown) => console.error('[crazygames] login failed', error));
      } else if (sessionRef.current?.chain === 'crazygames') {
        onSignedOut();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, exchange, onSignedOut, sessionRef]);

  /** Open CrazyGames' own login prompt (only ever from a tap), then sign in to the account. */
  const loginWithCrazyGames = useCallback(async (): Promise<void> => {
    const module = await crazyGamesUser();
    if (!module) throw new Error(CRAZYGAMES_LOGIN_UNAVAILABLE);
    let user: CrazyGamesUser | null;
    try {
      user = await module.showAuthPrompt();
    } catch (error) {
      const code = promptErrorCode(error);
      if (code === 'userAlreadySignedIn') user = await module.getUser();
      else if (code && QUIET_PROMPT_ERRORS.includes(code)) return;
      else throw error;
    }
    if (user) await exchange(user);
  }, [exchange]);

  return { crazyGamesLoginAvailable: enabled && available, loginWithCrazyGames };
}
